import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { business, navigation } = useSiteSettings();

  // Only render visible items, sorted by order. An item whose path
  // starts with '/#' is treated as an in-page anchor.
  const menu = (navigation || [])
    .filter((n) => n.visible)
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((n) => {
      const path = n.path || '';
      const isAnchor = path.startsWith('/#') || path.startsWith('#');
      const isRoute = !isAnchor && path.startsWith('/');
      const href = isAnchor ? (path.startsWith('/') ? path.slice(1) : path) : path;
      return { key: n.key, name: n.label, href, isRoute, isAnchor };
    });

  const handleNavClick = (e, item) => {
    if (item.isRoute) return;
    if (!item.isAnchor) return;
    e.preventDefault();
    const anchor = item.href.startsWith('#') ? item.href : `#${item.href}`;
    if (window.location.pathname !== '/') {
      window.location.href = '/' + anchor;
    } else {
      const el = document.querySelector(anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
      }
    }
  };

  return (
    <header
      className="fixed left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-gray-400/20 transition-all"
      style={{ top: 'var(--site-banner-h, 0px)' }}
      data-testid="site-header"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <span className="text-white font-bold text-2xl">{business?.name || 'TayLord Arms LLC'}</span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-1">
            {menu.map((item) => (
              item.isRoute ? (
                <Link key={item.key} to={item.href} data-testid={`nav-${item.key}`}
                  className="text-gray-300 hover:text-gray-100 px-3 py-2 text-sm font-medium transition-colors duration-200">
                  {item.name}
                </Link>
              ) : (
                <a key={item.key} href={item.href} data-testid={`nav-${item.key}`}
                  onClick={(e) => handleNavClick(e, item)}
                  className="text-gray-300 hover:text-gray-100 px-3 py-2 text-sm font-medium transition-colors duration-200">
                  {item.name}
                </a>
              )
            ))}
          </div>

          {/* Contact info */}
          <div className="hidden xl:flex items-center space-x-4">
            <a href={`tel:${business?.phone || ''}`} data-testid="header-phone"
              className="flex items-center space-x-2 text-gray-300 hover:text-gray-100 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm">{business?.phone}</span>
            </a>
            <a href="#quote"
              onClick={(e) => handleNavClick(e, { href: '#contact', isAnchor: true })}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200">
              Get Quote
            </a>
          </div>

          <div className="lg:hidden">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-300 hover:text-white p-2" aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-700">
            <div className="flex flex-col space-y-2">
              {menu.map((item) => (
                item.isRoute ? (
                  <Link key={item.key} to={item.href} onClick={() => setMobileMenuOpen(false)}
                    className="text-gray-300 hover:text-gray-100 px-3 py-2 text-base font-medium transition-colors">
                    {item.name}
                  </Link>
                ) : (
                  <a key={item.key} href={item.href} onClick={(e) => handleNavClick(e, item)}
                    className="text-gray-300 hover:text-gray-100 px-3 py-2 text-base font-medium transition-colors">
                    {item.name}
                  </a>
                )
              ))}
              <div className="flex flex-col space-y-2 pt-4 border-t border-slate-700 px-3">
                <a href={`tel:${business?.phone || ''}`} className="flex items-center space-x-2 text-gray-300 hover:text-gray-100 transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>{business?.phone}</span>
                </a>
                <a href="#quote"
                  onClick={(e) => handleNavClick(e, { href: '#contact', isAnchor: true })}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-center font-medium transition-colors">
                  Get Quote
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
